import { useEffect, useRef, type JSX } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { AttachmentUploadViewModel, useAttachmentUploadViewModel } from './attachment-upload.viewmodel';
import type { AttachmentUploadProps } from './attachment.types';

export const AttachmentUpload = ({
  source,
  caseComplaintId,
  isUploading,
  disabled = false,
  onUpload,
}: AttachmentUploadProps): JSX.Element => {
  const [state, viewModel] = useAttachmentUploadViewModel(source, onUpload, caseComplaintId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    viewModel.setUploading(isUploading);
  }, [isUploading, viewModel]);
  useEffect(() => {
    viewModel.setDisabled(disabled);
  }, [disabled, viewModel]);

  const openFilePicker = () => {
    if (viewModel.canUpload()) fileInputRef.current?.click();
  };

  return (
    <div className='attachment-upload'>
      <div
        className={`attachment-dropzone${state.isDragOver ? ' attachment-dropzone-active' : ''}${!viewModel.canUpload() ? ' attachment-dropzone-disabled' : ''}`}
        onDragOver={viewModel.handleDragOver}
        onDragLeave={viewModel.handleDragLeave}
        onDrop={viewModel.handleDrop}
        onClick={openFilePicker}
        role='button'
        tabIndex={!viewModel.canUpload() ? -1 : 0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') openFilePicker();
        }}
      >
        {state.isUploading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <CloudUploadIcon className='attachment-dropzone-icon' />
            <span className='attachment-dropzone-text'>Sleep bestanden hierheen of klik om te uploaden</span>
            <span className='attachment-dropzone-hint'>
              Max {AttachmentUploadViewModel.MAX_FILE_SIZE / 1024 / 1024} MB per bestand
            </span>
          </>
        )}
      </div>

      <input
        ref={fileInputRef}
        type='file'
        multiple
        accept={AttachmentUploadViewModel.ALLOWED_TYPES.join(',')}
        onChange={viewModel.handleInputChange}
        style={{ display: 'none' }}
      />

      {state.error && <p className='attachment-upload-error'>{state.error}</p>}
    </div>
  );
};
